import { useMemo } from 'react';
import type { AreaDto } from '@app/shared';

/** «Без участка» — отдельный вариант фильтра. */
export const NO_AREA = '__none';

/** Участок и всё, что в него вложено. */
function subtree(areas: AreaDto[], id: string): Set<string> {
  const kids = new Map<string, string[]>();
  for (const a of areas) if (a.parentId) kids.set(a.parentId, [...(kids.get(a.parentId) ?? []), a.id]);
  const out = new Set<string>();
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    if (out.has(cur)) continue;
    out.add(cur);
    stack.push(...(kids.get(cur) ?? []));
  }
  return out;
}

/** Подходит ли запись с участком areaId под выбранный фильтр. */
export function useAreaMatch(areas: AreaDto[], value: string | null): (areaId: string | null) => boolean {
  return useMemo(() => {
    if (!value || (value !== NO_AREA && !areas.some((a) => a.id === value))) return () => true;
    if (value === NO_AREA) return (id: string | null) => id === null;
    const set = subtree(areas, value);
    return (id: string | null) => !!id && set.has(id);
  }, [areas, value]);
}

/**
 * Фильтр кнопками сверху. Первый ряд — цеха/участки верхнего уровня, второй — сразу все линии
 * (участки, где стоит оборудование): линию можно выбрать одним нажатием, не выбирая сначала участок.
 * Выбран участок — во втором ряду только его линии. Показываются только ветки, где что-то есть.
 */
export function AreaFilter({ areas, used, value, onChange }: { areas: AreaDto[]; used: (string | null)[]; value: string | null; onChange: (v: string | null) => void }) {
  const { top, groups, topOf } = useMemo(() => {
    const byId = new Map(areas.map((a) => [a.id, a]));
    const order = (a: AreaDto, b: AreaDto) => a.sort - b.sort || a.name.localeCompare(b.name, 'ru');
    const rootOf = (id: string): string => {
      let cur = byId.get(id);
      for (let i = 0; cur?.parentId && byId.has(cur.parentId) && i < 20; i++) cur = byId.get(cur.parentId);
      return cur?.id ?? id;
    };
    const direct = new Set(used.filter((u): u is string => !!u && byId.has(u)));
    const topIds = new Set([...direct].map(rootOf));
    const top = areas.filter((a) => topIds.has(a.id)).sort(order);
    // Линии — участки с оборудованием, кроме самого верхнего уровня (он уже в первом ряду).
    const groups = areas.filter((a) => direct.has(a.id) && !topIds.has(a.id)).sort((a, b) => {
      const ra = byId.get(rootOf(a.id))!;
      const rb = byId.get(rootOf(b.id))!;
      return order(ra, rb) || order(a, b);
    });
    return { top, groups, topOf: rootOf };
  }, [areas, used]);

  const hasNoArea = used.some((u) => u === null);
  if (top.length === 0 && !hasNoArea) return null;
  const known = value && value !== NO_AREA && areas.some((a) => a.id === value) ? value : null;
  const selTop = known ? topOf(known) : null;
  const shownGroups = selTop ? groups.filter((g) => topOf(g.id) === selTop) : groups;

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="chips scroll" role="group" aria-label="Участок">
        <button type="button" className="chip" aria-pressed={!value} onClick={() => onChange(null)}>
          Все
        </button>
        {top.map((a) => (
          <button key={a.id} type="button" className="chip" aria-pressed={selTop === a.id} onClick={() => onChange(a.id)}>
            {a.name}
          </button>
        ))}
        {hasNoArea && (
          <button type="button" className="chip" aria-pressed={value === NO_AREA} onClick={() => onChange(NO_AREA)}>
            Без участка
          </button>
        )}
      </div>
      {shownGroups.length > 1 && (
        <div className="chips scroll" role="group" aria-label="Линия">
          {shownGroups.map((g) => (
            <button key={g.id} type="button" className="chip" aria-pressed={value === g.id} onClick={() => onChange(value === g.id ? selTop : g.id)}>
              {g.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Выбор фильтра запоминается на телефоне отдельно для каждого экрана. */
export function loadFilter(key: string): string | null {
  try {
    return localStorage.getItem(`filter:${key}`) || null;
  } catch {
    return null;
  }
}
export function saveFilter(key: string, v: string | null) {
  try {
    if (v) localStorage.setItem(`filter:${key}`, v);
    else localStorage.removeItem(`filter:${key}`);
  } catch {
    /* нет хранилища */
  }
}
